import React, { useCallback, useEffect, useRef, useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PublishIcon from "@mui/icons-material/Publish";
import SaveIcon from "@mui/icons-material/Save";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { BlogBreadcrumbs } from "../components/BlogBreadcrumbs";
import { ImageBlockUploader } from "../components/ImageBlockUploader";
import { PublishedByBadge } from "../components/PublishedByBadge";
import { RichTextEditor } from "../components/RichTextEditor";
import { SeoMetaForm } from "../components/SeoMetaForm";
import {
  fetchPostById,
  createPost,
  updatePost,
  updatePostStatus,
  saveRichText,
  clearCurrentPost,
} from "../blogSlice";
import { fetchCategories } from "../categorySlice";
import { fetchTags } from "../tagSlice";
import { inputSx } from "../blogTheme";

const EMPTY_SEO = {
  metaTitle: "",
  metaDescription: "",
  canonicalUrl: "",
  focusKeyword: "",
  keywords: [],
  ogTitle: "",
  ogDescription: "",
  ogImage: "",
  twitterCard: "summary",
  twitterTitle: "",
  twitterDescription: "",
  twitterCreator: "",
  noIndex: false,
  noFollow: false,
};

const EMPTY_FORM = {
  title: "",
  status: "draft",
  excerpt: "",
  scheduledFor: null,
  category: null,
  tags: [],
  isFeatured: false,
  coverImage: "",
  seo: EMPTY_SEO,
};

const paperSx = {
  p: 2,
  mb: 2,
  borderRadius: "24px",
  boxShadow: "3px 2px 20px 0px rgba(0,0,0,0.04)",
};

export default function PostEditor() {
  const { postId } = useParams();
  const isEdit = !!postId;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    currentPost,
    loadingPost,
    loadingSave,
    loadingPublish,
    loadingRichText,
    error,
  } = useSelector((state) => state.blog);
  const { categories, loading: loadingCategories } = useSelector(
    (state) => state.category,
  );
  const { tags, loading: loadingTags } = useSelector((state) => state.tag);

  const [form, setForm] = useState(EMPTY_FORM);
  const [richHtml, setRichHtml] = useState("");
  const editorRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchTags());
    if (isEdit) dispatch(fetchPostById(postId));
    return () => {
      dispatch(clearCurrentPost());
    };
  }, [postId]);

  useEffect(() => {
    if (isEdit && currentPost) {
      setForm({
        title: currentPost.title || "",
        status: currentPost.status || "draft",
        excerpt: currentPost.excerpt || "",
        scheduledFor: currentPost.scheduledFor
          ? dayjs(currentPost.scheduledFor)
          : null,
        category: currentPost.category || null,
        tags: currentPost.tags || [],
        isFeatured: !!currentPost.isFeatured,
        coverImage:
          typeof currentPost.coverImage === "string"
            ? currentPost.coverImage
            : currentPost.coverImage?.url || "",
        seo: {
          ...EMPTY_SEO,
          ...(currentPost.seo || {}),
          ogImage:
            typeof currentPost.seo?.ogImage === "string"
              ? currentPost.seo.ogImage
              : currentPost.seo?.ogImage?.url || "",
        },
      });
      setRichHtml(currentPost.richText?.contentHtml || "");
    }
  }, [currentPost?._id]);

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSeoChange = (field, value) =>
    setForm((prev) => ({ ...prev, seo: { ...prev.seo, [field]: value } }));

  const handleRichChange = (html) => {
    setRichHtml(html);
    if (isEdit && postId) {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const editor = editorRef.current?.getEditor();
        const contentJson = editor
          ? JSON.stringify(editor.getContents())
          : null;
        dispatch(saveRichText({ id: postId, contentJson }));
      }, 1500);
    }
  };

  const buildPayload = () => ({
    title: form.title,
    status: form.status,
    excerpt: form.excerpt,
    category: form.category?._id || form.category || undefined,
    tags: form.tags.map((t) => t._id || t),
    isFeatured: form.isFeatured,
    coverImage: form.coverImage ? { url: form.coverImage } : undefined,
    scheduledFor:
      form.status === "scheduled" && form.scheduledFor
        ? form.scheduledFor.toISOString()
        : undefined,
    seo: form.seo,
    richText: { contentHtml: richHtml },
  });

  const handleSave = async () => {
    const payload = buildPayload();
    if (isEdit) {
      dispatch(updatePost({ id: postId, data: payload }));
    } else {
      const result = await dispatch(createPost(payload));
      if (createPost.fulfilled.match(result)) {
        const post = result.payload?.post || result.payload;
        if (post?._id) navigate(`/blog/posts/${post._id}/edit`);
      }
    }
  };

  const handlePublish = () => {
    if (isEdit) dispatch(updatePostStatus({ id: postId, status: "published" }));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <BlogBreadcrumbs
          items={[
            { label: "Blog", href: "/blog" },
            { label: "Posts", href: "/blog/posts" },
            { label: isEdit ? "Edit Post" : "New Post" },
          ]}
        />

        {loadingPost && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <CircularProgress size={20} sx={{ color: "#065F46" }} />
            <Typography
              variant="body2"
              sx={{ fontFamily: "Heebo", color: "#6B7280" }}
            >
              Loading post…
            </Typography>
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={paperSx}>
              <TextField
                label="Title"
                value={form.title}
                onChange={(e) => handleChange("title", e.target.value)}
                fullWidth
                required
                size="small"
                sx={inputSx}
              />
            </Paper>
            <Paper
              sx={{
                mb: 2,
                borderRadius: "24px",
                boxShadow: "3px 2px 20px 0px rgba(0,0,0,0.04)",
                overflow: "hidden",
              }}
            >
              <RichTextEditor
                ref={editorRef}
                value={richHtml}
                onChange={handleRichChange}
                placeholder="Write your post content here…"
              />
            </Paper>
            <Accordion
              sx={{
                borderRadius: "24px !important",
                boxShadow: "3px 2px 20px 0px rgba(0,0,0,0.04)",
                mb: 2,
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#362B24" }} />}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "#362B24",
                    fontFamily: "Heebo",
                  }}
                >
                  SEO
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <SeoMetaForm values={form.seo} onChange={handleSeoChange} />
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={paperSx}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: "#362B24",
                  fontFamily: "Heebo",
                  mb: 1,
                }}
              >
                Publish
              </Typography>

              <TextField
                label="Status"
                value={form.status}
                onChange={(e) => handleChange("status", e.target.value)}
                size="small"
                fullWidth
                select
                sx={{ mb: 2, ...inputSx }}
              >
                {["draft", "published", "scheduled", "archived"].map((s) => (
                  <MenuItem key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </MenuItem>
                ))}
              </TextField>

              {form.status === "scheduled" && (
                <DateTimePicker
                  label="Scheduled For"
                  value={form.scheduledFor}
                  onChange={(val) => handleChange("scheduledFor", val)}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      sx: { mb: 2, ...inputSx },
                    },
                  }}
                />
              )}

              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <Button
                  startIcon={
                    loadingSave ? (
                      <CircularProgress size={16} sx={{ color: "#065F46" }} />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={handleSave}
                  disabled={loadingSave || !form.title}
                  fullWidth
                  sx={{
                    borderRadius: "29px",
                    border: "1px solid #065F46",
                    color: "#065F46",
                    fontFamily: "Heebo",
                    "&:hover": { bgcolor: "rgba(6,95,70,0.04)" },
                    "&:disabled": { opacity: 0.5 },
                  }}
                >
                  {loadingSave ? "Saving…" : "Save"}
                </Button>
                {isEdit && currentPost?.status !== "published" && (
                  <Button
                    startIcon={
                      loadingPublish ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : (
                        <PublishIcon />
                      )
                    }
                    onClick={handlePublish}
                    disabled={loadingPublish}
                    fullWidth
                    sx={{
                      background:
                        "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)",
                      color: "#fff",
                      borderRadius: "29px",
                      boxShadow: "1px 2px 30px 0px rgba(0,0,0,0.06)",
                      fontFamily: "Heebo",
                      "&:hover": { opacity: 0.9 },
                      "&:disabled": { opacity: 0.5 },
                    }}
                  >
                    {loadingPublish ? "Publishing…" : "Publish"}
                  </Button>
                )}
              </Box>

              {loadingRichText && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 1.5,
                  }}
                >
                  <CircularProgress size={12} sx={{ color: "#6B7280" }} />
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: "Heebo", color: "#6B7280" }}
                  >
                    Auto-saving…
                  </Typography>
                </Box>
              )}

              {isEdit && currentPost?.publishedBy && (
                <Box mt={2}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#362B24",
                      opacity: 0.55,
                      fontFamily: "Heebo",
                    }}
                  >
                    Published by
                  </Typography>
                  <PublishedByBadge user={currentPost.publishedBy} />
                </Box>
              )}
            </Paper>

            <Paper sx={paperSx}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: "#362B24",
                  fontFamily: "Heebo",
                  mb: 1,
                }}
              >
                Category & Tags
              </Typography>
              <Autocomplete
                options={categories}
                getOptionLabel={(c) => c.name || ""}
                value={form.category}
                onChange={(_, val) => handleChange("category", val)}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                loading={loadingCategories}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    size="small"
                    sx={inputSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCategories && (
                            <CircularProgress
                              size={16}
                              sx={{ color: "#065F46", mr: 1 }}
                            />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ mb: 2 }}
              />
              <Autocomplete
                multiple
                options={tags}
                getOptionLabel={(t) => t.name || ""}
                value={form.tags}
                onChange={(_, val) => handleChange("tags", val)}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                loading={loadingTags}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    size="small"
                    sx={inputSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingTags && (
                            <CircularProgress
                              size={16}
                              sx={{ color: "#065F46", mr: 1 }}
                            />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Paper>

            <Paper sx={paperSx}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: "#362B24",
                  fontFamily: "Heebo",
                  mb: 1,
                }}
              >
                Settings
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isFeatured}
                    onChange={(e) =>
                      handleChange("isFeatured", e.target.checked)
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#065F46",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        { bgcolor: "#065F46" },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontFamily: "Heebo", color: "#362B24" }}>
                    Featured Post
                  </Typography>
                }
              />
              <TextField
                label="Excerpt"
                value={form.excerpt}
                onChange={(e) => handleChange("excerpt", e.target.value)}
                size="small"
                fullWidth
                multiline
                rows={3}
                sx={{ mt: 1, ...inputSx }}
              />
            </Paper>

            <Paper sx={paperSx}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: "#362B24",
                  fontFamily: "Heebo",
                  mb: 1,
                }}
              >
                Cover Image
              </Typography>
              <ImageBlockUploader
                label="Upload Cover"
                existingUrl={form.coverImage}
                postId={postId}
                onUpload={(url) => handleChange("coverImage", url)}
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
}
